import React, { useState, useEffect, useRef } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Paper,
    Box,
    Typography,
    TableSortLabel,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DropDownFilter, { Option } from "./DropDownFilter";
import PaginationComponent from "../../brands/Pagination";
import BulkUploadFilter from "./BulkuploadFilter";
import Export from "../../../../src/assests/images/export.svg";
import Sorting from "../../../../src/assests/images/Sorting.svg";
import { exportActivityLogs, getExportRetailers } from "../../reports/api";
import { getToken } from "../../../utils/utils";

// 🧾 Column dropdown options (Log specific)
const columns: Option[] = [
    { label: "User ID", value: "id" },
    { label: "PMI Entity Code", value: "pmi_entity_codes" },
    { label: "Date and Time", value: "created_at" },
    { label: "Type", value: "type" },
    { label: "Description", value: "description" },
    { label: "File Name", value: "original_file_name" },
];

// 🧩 Interface for Log data
export interface LogEntry {
    id: string;
    pmi_entity_codes: string;
    created_at: string;
    type: string;
    description: string;
    original_file_name: string;
    status?: string;
    remarks?: string;
}

// Props
interface LogTableProps {
    data: LogEntry[];
    totalUsers?: number;
    page: number;
    setPage: (p: number) => void;
    limit: number;
    search: string;
    startDate: Date | null;
    endDate: Date | null;
}

// ✅ Default columns for log view
const defaultColumns = [
    "id",
    "pmi_entity_codes",
    "created_at",
    "type",
    "description",
    "original_file_name",
];

const LogTable: React.FC<LogTableProps> = ({
    data,
    totalUsers,
    page,
    setPage,
    limit,
    search,
    startDate,
    endDate,
}) => {
    const [users, setUsers] = useState<LogEntry[]>([]);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const popupRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const [selectedCols, setSelectedCols] = useState<string[]>(defaultColumns);

    // 🧠 Start with no sorting applied
    const [orderBy, setOrderBy] = useState<keyof LogEntry | null>(null);
    const [order, setOrder] = useState<"asc" | "desc">("asc");

    const [appliedFilters, setAppliedFilters] = useState<any>({
        lastLogin: "",
        startDate: "",
        endDate: "",
        search: "",
    });

    const token = getToken();

    // 🧠 Close popup when clicked outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsPopupVisible(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // 🧠 Load users data
    useEffect(() => {
        setUsers(data);
    }, [data]);

    const handleExportActivityLogs = async () => {
        try {
            const reqBody = {
                dateRange: {
                    from: startDate ? startDate.toISOString().split("T")[0] : "",
                    to: endDate ? endDate.toISOString().split("T")[0] : "",
                },
                search: search || "",
                columns: selectedCols,
            };
            console.log("Export request body >>>", reqBody);

            const response = await exportActivityLogs(reqBody, token);

            // Create download link for Excel
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "activity_logs_export.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Export failed:", error);
        }
    };

    // 🔽 Export CSV handler (unused, but kept for reference)
    const handleExport = async () => {
        try {
            const reqBody = {
                ...appliedFilters,
                search: search || "",
                startDate,
                endDate,
                columns: selectedCols,
            };
            console.log("Export request body >>>", reqBody);

            const response = await getExportRetailers(reqBody, token);

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "logs_export.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Export failed:", error);
        }
    };

    // 🔽 Sort logic (only activates when a column is clicked)
    const handleSort = (property: keyof LogEntry) => {
        if (orderBy === property) {
            // Toggle between ascending/descending
            setOrder(order === "asc" ? "desc" : "asc");
        } else {
            // New column, start with ascending
            setOrderBy(property);
            setOrder("asc");
        }
    };

    // 🔽 Apply sorting only if orderBy is set
    const sortedUsers = orderBy
        ? [...users].sort((a, b) => {
              const rawA = a[orderBy] ?? "";
              const rawB = b[orderBy] ?? "";

              const numA = Number(rawA);
              const numB = Number(rawB);

              if (!isNaN(numA) && !isNaN(numB)) {
                  return order === "asc" ? numA - numB : numB - numA;
              }

              const sA = String(rawA).toLowerCase();
              const sB = String(rawB).toLowerCase();
              if (sA < sB) return order === "asc" ? -1 : 1;
              if (sA > sB) return order === "asc" ? 1 : -1;
              return 0;
          })
        : users; // 👈 If no sort applied, use original order

    const paginatedUsers = sortedUsers || [];

    return (
        <Box
            sx={{
                background: "#fff",
                borderRadius: "10px",
                border: "1px solid #cfcfcf",
            }}
        >
            {/* Header with filters */}
            <Box
                sx={{
                    padding: "25px",
                    display: "flex",
                    justifyContent: "space-between",
                }}
            >
                <Typography sx={{ fontSize: "16px", fontWeight: "600", mt: "10px" }}>
                    Logs Total {totalUsers}
                </Typography>

                <Box sx={{ display: "flex" }}>
                    {/* Export */}
                    <Button
                        variant="contained"
                        sx={{
                            textTransform: "capitalize",
                            backgroundColor: "#fff",
                            fontSize: "14px",
                            fontWeight: "500",
                            height: "40px",
                            p: "16px",
                            borderRadius: "10px",
                            color: "#C8172F",
                            border: "1px solid #C8172F",
                            boxShadow: "none",
                        }}
                        startIcon={<img src={Export} alt="export" />}
                        onClick={handleExportActivityLogs}
                    >
                        Export CSV
                    </Button>
                </Box>
            </Box>

            {/* Table */}
            <Box>
                <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                    <Table>
                        <TableHead sx={{ background: "#EEF4FF" }}>
                            <TableRow>
                                {columns
                                    .filter((col) => selectedCols.includes(col.value))
                                    .map((col) => (
                                        <TableCell key={col.value}>
                                            <TableSortLabel
                                                active={orderBy === col.value}
                                                direction={
                                                    orderBy === col.value ? order : "asc"
                                                }
                                                onClick={() =>
                                                    handleSort(col.value as keyof LogEntry)
                                                }
                                                IconComponent={() => (
                                                    <img
                                                        src={Sorting}
                                                        alt="sort"
                                                        style={{ width: 16, height: 16 }}
                                                    />
                                                )}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedUsers.length > 0 ? (
                                paginatedUsers.map((user, idx) => (
                                    <TableRow key={idx}>
                                        {columns
                                            .filter((col) => selectedCols.includes(col.value))
                                            .map((col) => (
                                                <TableCell
                                                    key={col.value}
                                                    sx={{ whiteSpace: "nowrap" }}
                                                >
                                                    {user[col.value as keyof LogEntry] ?? "-"}
                                                </TableCell>
                                            ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={selectedCols.length || columns.length}
                                        align="center"
                                    >
                                        No Logs Found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Pagination */}
            <PaginationComponent
                page={page}
                limit={limit}
                totalPages={Math.ceil((totalUsers || 0) / limit)}
                setPage={setPage}
            />
        </Box>
    );
};

export default LogTable;
